<script lang="ts">
    import UIcon from 'shared/ui-widgets/uicons/UIcon.svelte';
    import {onMount} from 'svelte';
    import {fly} from 'svelte/transition';
    import {ignoreUnusedProperties, isTouchDevice} from 'shared/utils/custom-utils';

    export let responsive = false;
    export let bordered = false;
    export let hoverRows = false;
    export let stripedRows = false;
    export let condensed = false;
    export let superCondensed = false;
    export let horizontallyDivided = false;
    export let colorAccented = false;
    export let columnHeadersCentered = false;
    export let popoverAware = false;
    export let stickyHeader = false;
    export let verticallyCentered = false;
    export let horizontallyCentered = false;
    export let additionalClasses = '';
    export let additionalContainerClasses = '';

    let touchDevice = isTouchDevice();

    let tableContainerElement: HTMLDivElement;
    let tableContainerScrollLeft = 0;
    let tableContainerScrollWidth = 0;
    let tableContainerWidth = 0;
    let horizontalOverflowing = false;
    let overflowLeft = false;
    let overflowRight = false;

    let windowHeight = 0;
    let windowWidth = 0;
    let windowScrollY = 0;

    let overflowButtonsTopOffset = 0;
    $: calculateOverflowButtonsTopOffset(windowHeight, windowWidth, windowScrollY);
    $: calculateTableOverflowValues(windowWidth);

    onMount(() => {
        calculateTableOverflowValues();
        calculateOverflowButtonsTopOffset(windowHeight, windowWidth, windowScrollY);
    });

    function calculateOverflowButtonsTopOffset(viewportHeight: number, viewportWidth: number, windowScroll: number) {
        ignoreUnusedProperties(windowScroll, viewportWidth);

        if (!tableContainerElement || !responsive) {
            return;
        }

        touchDevice = isTouchDevice();

        const tableRect = tableContainerElement.getBoundingClientRect();
        const isTopOverflowing = tableRect.top < 0;
        const isBottomOverflowing = tableRect.bottom > viewportHeight;

        if (tableRect.top > viewportHeight) {
            return;
        }

        if (isTopOverflowing && isBottomOverflowing) {
            const topOffset = Math.abs(tableRect.top);
            overflowButtonsTopOffset = topOffset + viewportHeight / 2;
        }

        if (!isTopOverflowing && isBottomOverflowing) {
            const availableViewportHeight = viewportHeight - tableRect.top;
            overflowButtonsTopOffset = availableViewportHeight / 2;
        }

        if (isTopOverflowing && !isBottomOverflowing) {
            const topOffset = Math.abs(tableRect.top);
            const availableViewportHeight = tableRect.bottom;
            overflowButtonsTopOffset = topOffset + availableViewportHeight / 2;
        }

        if (!isTopOverflowing && !isBottomOverflowing) {
            overflowButtonsTopOffset = tableRect.height / 2;
        }
    }

    const calculateTableOverflowValues = (viewportWidth?: number) => {
        ignoreUnusedProperties(viewportWidth);

        if (!tableContainerElement || !responsive) {
            return;
        }

        tableContainerScrollLeft = tableContainerElement.scrollLeft;
        tableContainerScrollWidth = tableContainerElement.scrollWidth;
        tableContainerWidth = tableContainerElement.clientWidth;

        horizontalOverflowing = tableContainerScrollWidth > tableContainerWidth;
        overflowLeft = tableContainerScrollLeft > 0;
        overflowRight = tableContainerScrollLeft + tableContainerWidth < tableContainerScrollWidth - 1;
    }

    const handleScrollLeftClick = () => {
        tableContainerElement.scrollTo({
            left: Math.max(0, tableContainerScrollLeft - tableContainerWidth),
            behavior: 'smooth'
        });
    }

    const handleScrollRightClick = () => {
        tableContainerElement.scrollTo({
            left: Math.min(Math.max(0, tableContainerScrollLeft + tableContainerWidth - 250), tableContainerScrollWidth - tableContainerWidth),
            behavior: 'smooth'
        });
    }
</script>

<svelte:window bind:scrollY={windowScrollY}
               bind:innerHeight={windowHeight}
               bind:innerWidth={windowWidth}/>

<div class="overflow-table-buttons-container"
     class:with-overflow-buttons={horizontalOverflowing && responsive}>

    <div class="table-container {additionalContainerClasses}"
         on:scroll={() => calculateTableOverflowValues()}
         bind:this={tableContainerElement}
         class:table-responsive={responsive}
         class:table-bordered={bordered}
         class:popover-aware={popoverAware}>

        <table class="table {additionalClasses}"
               class:table-hover={hoverRows}
               class:table-striped={stripedRows}
               class:table-horizontally-divided={horizontallyDivided}
               class:table-condensed={condensed}
               class:table-super-condensed={superCondensed}
               class:table-color-accented={colorAccented}
               class:table-centered-column-headers={columnHeadersCentered}
               class:table-sticky-header={stickyHeader}
               class:table-vertically-centered-data={verticallyCentered}
               class:table-horizontally-centered-data={horizontallyCentered}>

            {#if $$slots.header}
                <thead>
                    <slot name="header"/>
                </thead>
            {/if}

            <tbody>
                <slot name="body"/>
            </tbody>

            {#if $$slots.footer}
                <tfoot>
                    <slot name="footer"/>
                </tfoot>
            {/if}
        </table>

        {#if responsive && horizontalOverflowing && !touchDevice}
            {#if overflowLeft}
                <button type="button"
                        aria-label="Left scroll"
                        style="top: {overflowButtonsTopOffset}px"
                        class="horizontal-overflow-btn scroll-left"
                        transition:fly={{x: -15}}
                        on:click={handleScrollLeftClick}>

                    <UIcon icon="arrow-small-left"/>
                </button>
            {/if}

            {#if overflowRight}
                <button type="button"
                        aria-label="Right scroll"
                        style="top: {overflowButtonsTopOffset}px"
                        class="horizontal-overflow-btn scroll-right"
                        transition:fly={{x: 15}}
                        on:click={handleScrollRightClick}>

                    <UIcon icon="arrow-small-right"/>
                </button>
            {/if}
        {/if}
    </div>
</div>

<style lang="less">
    @import (reference) "styles/portaro.media-queries.less";
    @import (reference) "styles/portaro.variables.less";
    @import (reference) "styles/portaro.themes.less";

    @overflowBtnSize: 38px;
    @overflowIconSize: 18px;

    .overflow-table-buttons-container {
        position: relative;

        &.with-overflow-buttons {
            overflow: hidden;
        }

        .horizontal-overflow-btn {
            position: absolute;
            width: @overflowBtnSize;
            height: @overflowBtnSize;
            border-radius: 50%;
            outline: 1px solid transparent;
            outline-offset: 1px;
            background-color: var(--table-header-accent-color);
            border: 1px solid rgba(0, 0, 0, 0.15);
            font-size: @overflowIconSize;
            display: flex;
            color: white;
            align-items: center;
            justify-content: center;
            transform: translateY(-50%);
            box-shadow: rgba(0, 0, 0, 0.2) 0 3px 8px;
            transition: opacity 0.3s ease-in-out, border-color 0.3s ease-in-out, outline-color 0.3s ease-in-out, visibility 0.3s ease-in-out, transform 0.3s ease-in-out;

            &:hover,
            &:active {
                opacity: 0.85;
                outline-color: var(--accent-blue-new);
            }

            &:active {
                opacity: 0.65;
            }

            &.scroll-left {
                left: 0;
                transform: translate(@spacing-ml, -50%);
            }

            &.scroll-right {
                right: 0;
                transform: translate(calc(@spacing-ml * -1), -50%);
            }
        }
    }

    .table-container.table-bordered {
        border: 1px solid @themed-border-default;
        border-radius: @border-radius-default;
    }

    .table {
        margin-bottom: 0;
    }

    .table-responsive {
        overflow-y: hidden;

        @media screen and (max-width: @screen-xs-max) {
            margin-bottom: 0;
        }

        &.popover-aware {
            min-height: 250px;
        }
    }

    .table-super-condensed {
        :global {
            > thead,
            > tbody,
            > tfoot {
                > tr {
                    > th {
                        padding: 3px 5px;
                        font-size: 12px;
                    }

                    > td {
                        padding: 0;
                        font-size: 12px;
                    }
                }
            }
        }
    }

    .table-sticky-header {
        border-collapse: separate;

        thead {
            background-color: @themed-body-bg;
            position: sticky;
            top: 0;
            z-index: 90;
        }
    }

    :global {
        .table-vertically-centered-data {
            tr > th,
            tr > td {
                vertical-align: middle !important;
            }
        }

        .table-horizontally-centered-data {
            tr > th,
            tr > td {
                text-align: center;
            }
        }

        .table-hover > tbody > tr:hover {
            transition: background-color 0.2s ease-in-out;
        }

        .table-color-accented > thead > tr > th {
            color: var(--table-header-accent-color);
            border-bottom: 2px solid var(--table-header-accent-color);
        }

        .table-centered-column-headers > thead > tr > th {
            text-align: center;
            vertical-align: middle;
            line-height: 1.25;
        }
    }
</style>